import React, { useEffect } from "react";
import { createPortal } from "react-dom";

// Reusable "almost full-screen" dialog shell — for flows too rich for
// EntityFormModal's compact form (e.g. a multi-step builder) that still
// need to present as an overlay above the current page rather than
// replacing it outright. All chrome (header/body/footer) is owned by the
// caller via `children`; this component only owns the overlay + panel.
//
// No backdrop-click-to-close on purpose: a full builder can hold a lot of
// unsaved work, so closing requires an explicit action (the caller's own
// close/back button, or Escape).
//
// Usage:
//   <FullScreenDialog isOpen={isOpen} onClose={onClose}>
//     <MyWizard onBack={onClose} />
//   </FullScreenDialog>
const FullScreenDialog = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/55 backdrop-blur-sm p-3 sm:p-6 lg:p-10">
      <div className="fade-in w-full h-full bg-surface border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {children}
      </div>
    </div>,
    document.body
  );
};

export default FullScreenDialog;
